import type { ReactNode } from "react";
import { CheckCircle2, FileJson2, FolderTree, Split, XCircle } from "lucide-react";
import type { SlideDef, SlideProps } from "@/engine/types";
import { RevealItem, RevealList, SlideShell, useAccent } from "@/ui";
import { TopicCard } from "./components/TopicCard";

const GHOST_URLS = [
  "?color=red",
  "?color=red&size=9",
  "?sort=price&page=4",
  "?size=9&width=wide",
  "?brand=stride&sort=new",
];

/** One category node fanning out into dozens of ghost parameter-URLs. */
function FacetExplosion() {
  const [from] = useAccent();
  return (
    <div className="flex flex-col items-center">
      <span
        className="rounded-full border px-5 py-1.5 font-mono text-xl text-white/90"
        style={{ borderColor: `${from}66`, background: `${from}14` }}
      >
        /running-shoes/
      </span>
      <svg viewBox="0 0 320 44" className="h-11 w-full" aria-hidden="true">
        {[16, 88, 160, 232, 304].map((x) => (
          <line
            key={x}
            x1={160}
            y1={0}
            x2={x}
            y2={44}
            stroke="rgba(255,255,255,0.18)"
            strokeWidth={1.5}
          />
        ))}
      </svg>
      <div className="flex flex-wrap justify-center gap-2.5">
        {GHOST_URLS.map((g) => (
          <span
            key={g}
            className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-1 font-mono text-xl text-white/45"
          >
            {g}
          </span>
        ))}
        <span className="px-2 py-1 font-mono text-xl text-accent-amber/90">+ dozens more…</span>
      </div>
    </div>
  );
}

function CheckRow({ good, children }: { good: boolean; children: ReactNode }) {
  const Icon = good ? CheckCircle2 : XCircle;
  return (
    <p className="flex items-start gap-3.5 text-2xl leading-relaxed text-white/70">
      <Icon
        className={`mt-1.5 h-6 w-6 shrink-0 ${good ? "text-accent-emerald" : "text-accent-rose"}`}
      />
      <span>{children}</span>
    </p>
  );
}

function EcommerceSeoSlide({ active }: SlideProps) {
  return (
    <SlideShell chapter={5} title="E-commerce SEO">
      <RevealList active={active} stagger={0.15} className="grid w-full grid-cols-3 gap-8">
        <RevealItem className="h-full">
          <TopicCard icon={FolderTree} title="Categories vs products">
            <p className="text-2xl leading-relaxed text-white/70">
              <span className="font-semibold text-white">Category pages</span> target broader
              queries.
            </p>
            <p className="text-2xl leading-relaxed text-white/70">
              <span className="font-semibold text-white">Product pages</span> target SKUs and
              long-tail.
            </p>
            <p className="text-2xl leading-relaxed text-white/70">
              Avoid <span className="font-semibold text-white">thin variants</span> as separate
              indexed URLs — if they add no value.
            </p>
          </TopicCard>
        </RevealItem>

        <RevealItem className="h-full">
          <TopicCard icon={Split} title="Faceted search explodes URLs">
            <FacetExplosion />
            <p className="text-xl leading-relaxed text-white/70">
              Use <span className="font-semibold text-white">filters, parameters & canonicals</span>{" "}
              carefully to avoid{" "}
              <span className="font-semibold text-accent-amber">index bloat</span>.
            </p>
          </TopicCard>
        </RevealItem>

        <RevealItem className="h-full">
          <TopicCard icon={FileJson2} title="Unique per product">
            <CheckRow good>
              <span className="font-semibold text-white">Unique titles and descriptions</span> per
              product.
            </CheckRow>
            <CheckRow good>
              <span className="font-semibold text-white">Product</span> +{" "}
              <span className="font-semibold text-white">Offer</span> JSON-LD — where valid.
            </CheckRow>
            <CheckRow good={false}>
              Don't <span className="font-semibold text-white">copy manufacturer text</span>{" "}
              blindly.
            </CheckRow>
          </TopicCard>
        </RevealItem>
      </RevealList>
    </SlideShell>
  );
}

export const ecommerceSeo: SlideDef = {
  id: "c5-ecommerce",
  chapter: 5,
  title: "E-commerce SEO",
  component: EcommerceSeoSlide,
};
